import { useState } from 'react';
import { CircleCheck } from 'lucide-react';
import { cn } from '@pixlide/ui';

interface Slide {
  title: string;
  description: string;
  bullets: readonly string[];
}

/**
 * Volets de la colonne vitrine.
 *
 * Le premier reprend mot pour mot la maquette ; les suivants tiennent la même promesse sur
 * les autres temps forts du produit. Ce sont des textes MARKETING, donc destinés à être
 * réécrits par l'équipe : ils sont regroupés ici, dans une seule constante, plutôt que
 * dispersés dans le JSX, pour qu'une relecture ne demande pas de lire du code.
 */
const SLIDES: readonly Slide[] = [
  {
    title: 'Créez et diffusez vos vidéos facilement grâce à Pixlide',
    description:
      'Une solution simple et professionnelle pour produire des contenus impactants et diffuser en toute simplicité',
    bullets: [
      'Vidéos conçues pour les menus boards',
      'Choix express ou production en moins de 48h',
      'Un résultat pro, pensé pour capter l’attention.',
      'Une solution fluide, simple à utiliser au quotidien.',
    ],
  },
  {
    title: 'Des modèles prêts à l’emploi, ou une création sur mesure',
    description:
      'Partez d’un template à personnaliser en quelques minutes, ou confiez votre projet à notre équipe de production.',
    bullets: [
      'Templates adaptés à votre activité',
      'Vos textes, vos tarifs, vos photos',
      'Création sur mesure réalisée par nos monteurs',
      'Reprise de vos brouillons à tout moment',
    ],
  },
  {
    title: 'Pilotez tous vos écrans depuis un seul tableau de bord',
    description:
      'Programmez vos diffusions à l’avance, écran par écran ou sur l’ensemble de vos points de vente.',
    bullets: [
      'Diffusion sur TV connectée ou boîtier Pixlide',
      'Programmation par jour et par plage horaire',
      'Plusieurs écrans pilotés en une seule fois',
      'Support et accompagnement inclus',
    ],
  },
];

const TESTIMONIAL =
  '“Un vrai gain de temps pour créer des vidéos professionnelles, impactantes et sans complexité.”';

/**
 * Colonne de droite des écrans d'authentification : argumentaire produit sur fond de trame
 * technique, avec les pastilles de navigation de la maquette.
 *
 * Masquée sous `lg` : sur mobile, l'écran doit servir à se connecter, pas à faire défiler
 * une brochure avant d'atteindre le formulaire.
 */
export function AuthShowcase() {
  const [index, setIndex] = useState(0);

  // `?? SLIDES[0]!` : `noUncheckedIndexedAccess` considère tout accès indexé comme
  // potentiellement absent. L'index est pourtant toujours borné par les pastilles, d'où ce
  // repli qui ne se produit jamais à l'exécution.
  const slide = SLIDES[index] ?? SLIDES[0]!;

  return (
    <aside className="relative hidden overflow-hidden bg-white lg:flex lg:w-[46%] lg:shrink-0 lg:flex-col">
      {/* Trame décorative, en fond et sans contenu : `aria-hidden` évite qu'elle soit
          parcourue par les lecteurs d'écran. */}
      <div aria-hidden className="auth-blueprint absolute inset-0" />

      <div className="relative flex flex-1 flex-col justify-center px-14 xl:px-20">
        <h2 className="max-w-md text-3xl leading-tight font-medium tracking-tight text-balance">
          {slide.title}
        </h2>

        <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-500">{slide.description}</p>

        <ul className="mt-8 flex max-w-md flex-col gap-3">
          {slide.bullets.map((bullet) => (
            <li key={bullet} className="flex items-start gap-2.5 text-sm text-ink-700">
              <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-ink-900" strokeWidth={1.5} />
              {bullet}
            </li>
          ))}
        </ul>

        <hr className="mt-10 max-w-md border-ink-100" />

        <blockquote className="mt-6 max-w-md text-sm leading-relaxed text-ink-500 italic">
          {TESTIMONIAL}
        </blockquote>
      </div>

      <div className="relative flex justify-center gap-1.5 pb-10">
        {SLIDES.map((item, position) => {
          const isActive = position === index;

          return (
            <button
              key={item.title}
              type="button"
              onClick={() => setIndex(position)}
              // Le libellé décrit la DESTINATION, pas la forme : « volet 2 sur 3 » reste
              // compréhensible sans voir les pastilles.
              aria-label={`Volet ${position + 1} sur ${SLIDES.length} : ${item.title}`}
              aria-current={isActive}
              className={cn(
                'h-1.5 rounded-pill transition-all',
                isActive ? 'w-6 bg-ink-900' : 'w-1.5 bg-ink-100 hover:bg-ink-300',
              )}
            />
          );
        })}
      </div>
    </aside>
  );
}
